import { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import FormGroup from "react-bootstrap/FormGroup";
import { isPossiblePhoneNumber } from "libphonenumber-js";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";

import FormBlock from "../../FormBlock";
import { useAppContext } from "../../../../_context";
import SignupField from "../../SignupField";
import Button from "../../../Misc/Button";
import SportSelection from "./SportSelection";
import TelephoneInput from "../../../TelephoneInput";
import FieldError from "../../../FieldError";
import { apiUrl, getId, query } from "../../../../utilities/Helpers";
import UploadFileTarget from "../../../Upload/UploadFileTarget";
import { validateEmail } from "../../../../utilities/validations";
import { currencyList } from "../../../../utilities/Currency";
import ReCaptcha from "../../../ReCaptcha";

const radiusRange = [...Array(20).keys()].map((i) => i + 1);
const PHONE_INPUT_ID = "signup-phone-number";

const FormSection = ({ setMessageSent }) => {
  const [{ tx }] = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [placesPostCode, setPlacesPostcode] = useState({});
  const [currency, setCurrency] = useState({
    cc: currencyList[0].cc,
    symbol: currencyList[0].symbol,
    name: currencyList[0].name,
  });
  const [recaptchaVerified, setRecaptchaVerified] = useState(false);
  const [coachId, setCoachId] = useState("");
  const units = [
    { value: "km", label: tx["Km"] },
    { value: "miles", label: tx["Miles"] },
  ];

  useEffect(() => {
    setCoachId(getId());

    return () => setCoachId("");
  }, []);

  const submitToApi = async (payload) => {
    const addresses = placesPostCode?.value?.place_id
      ? await geocodeByPlaceId(placesPostCode.value.place_id)
      : [];
    const address = addresses && addresses?.length ? addresses[0] : {};

    try {
      return await fetch(apiUrl("/api/v1/web/coach"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-version": "New",
        },
        body: JSON.stringify({
          ...payload,
          location: {
            address: address?.formatted_address,
            lat: address?.geometry?.location?.lat(),
            lng: address?.geometry?.location?.lng(),
          },
          currency,
        }),
      });
    } catch (e) {
      console.error(e);
      setErrors({ "generic-error": tx["Please check the form and try again"] });
    }
  };

  const validate = (payload) => {
    const newErrors = {};
    let firstError = null;

    if (!payload.profileUrl) {
      newErrors["signup-avatar-url"] = tx["Please select an image"];
      firstError = "signup-avatar-url";
    }

    if (!payload.firstName) {
      newErrors["signup-first-name"] = null;
      firstError = "signup-first-name";
    }

    if (!payload.lastName) {
      newErrors["signup-last-name"] = null;
      firstError = firstError || "signup-last-name";
    }

    if (!validateEmail(payload.email)) {
      newErrors["signup-email"] = tx["Please enter a valid email address"];
      firstError = firstError || "signup-email";
    }

    if (!payload.phone) {
      newErrors[PHONE_INPUT_ID] = null;
      firstError = firstError || PHONE_INPUT_ID;
    }

    try {
      if (!isPossiblePhoneNumber(payload.phone)) {
        newErrors[PHONE_INPUT_ID] = tx["Please enter a valid phone number"];
        firstError = firstError || PHONE_INPUT_ID;
      }
    } catch (e) {
      if (!isPossiblePhoneNumber(payload.phone)) {
        newErrors[PHONE_INPUT_ID] = tx["Please enter a valid phone number"];
        firstError = firstError || PHONE_INPUT_ID;
      }
    }

    if (!payload.headline) {
      newErrors["signup-headline"] = null;
      firstError = firstError || "signup-headline";
    }

    if (!payload.description) {
      newErrors["signup-about"] = null;
      firstError = firstError || "signup-about";
    }

    if (Object.keys(JSON.parse(payload.postCode)).length === 0) {
      newErrors["signup-postcode"] = null;
      firstError = firstError || "signup-postcode";
    }

    if (radiusRange.includes(payload.radius)) {
      newErrors["signup-radius"] = null;
      firstError = firstError || "signup-radius";
    }

    if (radiusRange.includes(payload.unit)) {
      newErrors["signup-radius-unit"] = null;
      firstError = firstError || "signup-radius-unit";
    }

    if (payload.price < 1) {
      newErrors["signup-price"] = null;
      firstError = firstError || "signup-price";
    }

    if (!recaptchaVerified) {
      newErrors["clubs-recaptcha"] =
        tx["Please complete the captcha to continue"];
      firstError = firstError || "clubs-recaptcha";
    }

    if (firstError) {
      const errorElem = query(`#${firstError}`);

      if (errorElem.type?.toLowerCase() === "hidden") {
        try {
          errorElem.parentNode.scrollIntoView({ block: "center" });
        } catch (e) {
          errorElem.focus();
        }
      } else {
        errorElem.focus();
      }

      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleServerValidationErrors = (errors = [{}]) => {
    const map = [
      { index: 0, key: "profileUrl", selector: "signup-avatar-url" },
      { index: 1, key: "firstName", selector: "signup-first-name" },
      { index: 2, key: "lastName", selector: "signup-last-name" },
      { index: 3, key: "email", selector: "signup-email" },
      { index: 4, key: "phone", selector: PHONE_INPUT_ID },
      { index: 5, key: "headline", selector: "signup-headline" },
      { index: 6, key: "description", selector: "signup-about" },
      { index: 7, key: "postCode", selector: "signup-postcode" },
      { index: 8, key: "radius", selector: "signup-radius" },
      { index: 9, key: "unit", selector: "signup-radius-unit" },
      { index: 10, key: "price", selector: "signup-price" },
      {
        index: 11,
        key: "qualifications",
        selector: "signup-coaching-qualifications",
      },
      { index: 12, key: "clubsVenues", selector: "signup-clubs-venues" },
    ];

    let firstIndex = null;
    let firstError = null;
    const output = {};

    errors.forEach((e) => {
      if (!e.field) {
        output["generic-error"] = tx["be_unexpected_error"];
      } else {
        const serverId = e.field.join(".");
        const mapItem = map.find((item) => item.key === serverId);
        const htmlId = mapItem?.selector;

        if (htmlId) {
          if (firstIndex === null || firstIndex > mapItem.index) {
            firstIndex = mapItem.index;
            firstError = htmlId;
          }

          output[htmlId] = e.error || null;
        } else {
          output["generic-error"] = tx["Please check the form and try again"];
        }
      }
    });

    firstError && query(`#${firstError}`).focus();
    setErrors(output);
  };

  const handleSecondaryServerValidationErrors = (errors) => {
    const output = [];

    for (let x in errors) {
      output.push({
        field: [x],
        error: errors[x].message,
      });
    }

    handleServerValidationErrors(output);
  };

  const submit = async () => {
    setIsLoading(true);
    const payload = {
      _id: coachId,
      profileUrl: query("#signup-avatar-url").value.trim(),
      largeAvatarUrl: query("#signup-large-avatar-url").value.trim(),
      sport: query(".sport-selection-item:checked").value.trim(),
      firstName: query("#signup-first-name").value.trim(),
      lastName: query("#signup-last-name").value.trim(),
      email: query("#signup-email").value.trim(),
      phone: query(`#${PHONE_INPUT_ID}`).value.trim(),
      headline: query("#signup-headline").value.trim(),
      description: query("#signup-about").value.trim(),
      postCode: query("#signup-postcode").value.trim(),
      radius: query("#signup-radius").value.trim(),
      unit: query("#signup-radius-unit").value.trim(),
      price: query("#signup-price").value.trim(),
      qualifications: query("#signup-coaching-qualifications").value.trim(),
      clubsVenues: query("#signup-clubs-venues").value.trim(),
    };

    if (!validate(payload)) {
      setIsLoading(false);
      return;
    }

    const response = await submitToApi(payload);
    const result = await response.json();

    setIsLoading(false);

    if (!response.ok) {
      if (!result.message) {
        return handleServerValidationErrors();
      }
      if (result.message === "validation error") {
        handleServerValidationErrors(result.errors);
        return;
      }

      if (result.message.match("validation failed")) {
        if (result.message.match("Partner validation failed: email")) {
          handleSecondaryServerValidationErrors({
            email: { message: tx["coach_sign_up_email_already_in_use"] },
          });
          return;
        }
        if (result.message.match("phone")) {
          /**
           * element with id `signup-phone-number` is not found
           * resulting in scrollToView action not being taken
           * so we are using a separate id to focus on the actual <PhoneInput/> component
           */
          query(`#phone-input-${PHONE_INPUT_ID}`).focus();

          handleSecondaryServerValidationErrors({
            phone: {
              message: tx["coach_sign_up_phone_already_in_use"],
            },
          });
          return;
        }

        handleSecondaryServerValidationErrors(result.errors);
        return;
      }

      return handleServerValidationErrors();
    }

    setMessageSent(true);
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  };

  const selectStyles = {
    dropdownIndicator: () => ({}),
    indicatorsContainer: () => ({
      display: "none",
    }),
  };

  const handleSelectCurrency = (e) => {
    const selectedCurrency = currencyList.find(
      (item) => item.cc === e.target.value,
    );
    setCurrency(selectedCurrency);
  };

  return (
    <section className="form-section mb-7">
      <Container>
        <Row>
          <Col md={10} lg={9} className="px-0 px-sm-4 mx-auto">
            <FormBlock label={tx["Pick a sport"]}>
              <SportSelection />
            </FormBlock>

            <FormBlock label={tx["Personal info"]}>
              <Col xs={12} className="text-center">
                <div className="upload-file-target">
                  <UploadFileTarget photoPrefix={coachId} />
                  <FieldError
                    fieldName="signup-avatar-url"
                    allErrors={errors}
                    className="pb-3 text-center"
                  />
                </div>
              </Col>

              <Col md={6}>
                <SignupField
                  id="first-name"
                  label={tx["First name"]}
                  mandatory={true}
                />
                <FieldError fieldName="signup-first-name" allErrors={errors} />
              </Col>

              <Col md={6}>
                <SignupField
                  id="last-name"
                  label={tx["Last name"]}
                  mandatory={true}
                />
                <FieldError fieldName="signup-last-name" allErrors={errors} />
              </Col>

              <Col md={6}>
                <SignupField
                  id="email"
                  label={tx["Email"]}
                  mandatory={true}
                  type="email"
                />
                <FieldError fieldName="signup-email" allErrors={errors} />
              </Col>

              <Col md={6}>
                <FormGroup className="mb-3">
                  <label className="form-label" htmlFor={PHONE_INPUT_ID}>
                    * {tx["Phone number"]}
                  </label>
                  <TelephoneInput id={PHONE_INPUT_ID} />
                  <FieldError fieldName={PHONE_INPUT_ID} allErrors={errors} />
                </FormGroup>
              </Col>

              <Col xs={12}>
                <SignupField
                  id="headline"
                  label={tx["coaches_signup:Headline"]}
                  mandatory={true}
                />
                <FieldError fieldName="signup-headline" allErrors={errors} />
              </Col>

              <Col xs={12}>
                <FormGroup className="mb-3">
                  <label className="form-label" htmlFor="signup-about">
                    * {tx["coaches_signup:About"]}
                  </label>
                  <textarea
                    id="signup-about"
                    className="form-control"
                    placeholder={tx["Tell us a bit more about you..."]}
                    rows={5}
                  />
                </FormGroup>
                <FieldError fieldName="signup-about" allErrors={errors} />
              </Col>
            </FormBlock>

            <FormBlock label={tx["Coaching info"]}>
              <Col xs={12} md={6}>
                <FormGroup className="mb-3 places-autocomplete-container">
                  <label className="form-label" htmlFor="signup-postcode">
                    * {tx["Postcode"]}
                  </label>
                  <GooglePlacesAutocomplete
                    apiKey={process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY}
                    selectProps={{
                      placesPostCode,
                      styles: selectStyles,
                      onChange: setPlacesPostcode,
                    }}
                    styles={selectStyles}
                  />
                  <input
                    type="hidden"
                    id="signup-postcode"
                    value={JSON.stringify(placesPostCode)}
                  />
                </FormGroup>
                <p className="form-extra-info">
                  {tx["Postcode where you'd prefer to give coaching lessons"]}
                </p>
                <FieldError fieldName="signup-postcode" allErrors={errors} />
              </Col>

              <Col xs={12} md={6}>
                <FormGroup className="mb-3">
                  <label className="form-label" htmlFor="signup-radius">
                    * {tx["Radius"]}
                  </label>
                  <Row>
                    <Col xs={12} md={6} className="mb-2">
                      <select
                        id="signup-radius"
                        className="form-control"
                        defaultValue={10}>
                        {radiusRange.map((radius, index) => (
                          <option key={index} value={radius}>
                            {radius}
                          </option>
                        ))}
                      </select>
                    </Col>
                    <Col xs={12} md={6}>
                      <select
                        id="signup-radius-unit"
                        className="form-control"
                        defaultValue={10}>
                        {units.map(({ label, value }, index) => (
                          <option key={index} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </Col>
                  </Row>
                </FormGroup>
                <p className="form-extra-info">
                  {tx["Radius around postcode (willingness to travel)"]}
                </p>
                <FieldError fieldName="signup-radius" allErrors={errors} />
              </Col>

              <Col xs={12} md={6}>
                <FormGroup className="mb-3">
                  <label className="form-label" htmlFor="signup-currency">
                    * {tx["Currency"]}
                  </label>
                  <select
                    id="signup-currency"
                    className="form-control"
                    onChange={handleSelectCurrency}>
                    {currencyList.map(({ cc, symbol, name }, index) => (
                      <option key={index} value={cc}>
                        {`(${symbol}) ${name}`}
                      </option>
                    ))}
                  </select>
                </FormGroup>
                <FieldError fieldName="signup-currency" allErrors={errors} />
              </Col>

              <Col xs={12} md={6}>
                <SignupField
                  id="price"
                  label={tx["Price"]}
                  type="number"
                  mandatory={true}
                />
                <p className="form-extra-info">
                  {
                    tx[
                      "Price per lesson, court not included (we don't take any fee)"
                    ]
                  }
                </p>
                <FieldError fieldName="signup-price" allErrors={errors} />
              </Col>
              <Col xs={12} md={6}>
                <SignupField id="clubs-venues" label={tx["Clubs/Venues"]} />
                <p className="form-extra-info">
                  {
                    tx[
                      "Clubs/Venues where you want to coach (name them comma separated)"
                    ]
                  }
                </p>
                <FieldError
                  fieldName="signup-clubs-venues"
                  allErrors={errors}
                />
              </Col>
              <Col xs={12} md={6}>
                <SignupField
                  id="coaching-qualifications"
                  label={tx["Coaching qualifications"]}
                />
                <p className="form-extra-info">
                  {
                    tx[
                      "If multiple, separate them with a comma; if none, leave it blank"
                    ]
                  }
                </p>
                <FieldError
                  fieldName="signup-coaching-qualifications"
                  allErrors={errors}
                />
              </Col>
            </FormBlock>
            <Col xs={12}>
              <FormBlock>
                <ReCaptcha
                  id="clubs-recaptcha"
                  className="d-flex justify-content-center"
                  onChange={setRecaptchaVerified}
                />
                <FieldError
                  className="text-center"
                  fieldName="clubs-recaptcha"
                  allErrors={errors}
                />
              </FormBlock>
            </Col>
            <Row>
              <Col className="text-center">
                <FieldError
                  fieldName="generic-error"
                  allErrors={errors}
                  className="mt-2"
                />
                <Button onClick={submit} isLoading={isLoading}>
                  {tx["coaches_signup:Sign up"]}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default FormSection;
